// @flow
/* eslint-disable react/no-multi-comp */
/* 💁 This component will always sit between scramble4 and scramble-com. It
provides what would usually be considered global styles, translations, and locale */
import * as React from 'react';
import styled, {
  ThemeProvider,
  type StyledComponent,
  type PropsWithTheme,
} from 'styled-components';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import QueryClientProvider from 'ui/query-client-provider';
import { theme } from 'ui/styles';
import type { Theme } from 'ui/styles';
import { derivedColors } from 'modules/client-theme';
import type { Translations } from 'modules/l10n';
import { memoize } from 'modules/memoize';
import { L10nProvider } from 'ui/l10n-context';
import { SettingsProvider } from 'ui/settings-context';
// types
import type { SettingsParams } from 'ui/settings-context';

export const BaseCssProvider: StyledComponent<
  {},
  Theme,
  HTMLDivElement,
> = styled.div`
  font-family: ${(props: PropsWithTheme<{ ... }, Theme>) =>
    props.theme['font-family']};
  font-size: 12px;
  color: ${({ theme: t }: PropsWithTheme<{ ... }, Theme>) => t.black};

  * {
    box-sizing: border-box;
    font-weight: unset;
  }

  code {
    background: ${({ theme: t }: PropsWithTheme<{ ... }, Theme>) => t.gray_1};
    border-radius: 5px;
    padding: 3px;
  }

  svg.lucide {
    stroke-width: 1.5;
    vertical-align: middle;
  }
`;

const getDisplayName = (WrappedComponent: React.AbstractComponent<any>) =>
  WrappedComponent.displayName || WrappedComponent.name || 'Component';

const ScrambleComErrorBoundary = ({ children }: { children: React.Node }) => (
  <SentryErrorBoundary
    fallback={() => (
      <span className="componentError">
        Something went wrong. Our engineers have been notified and will resolve
        the issue promptly.
      </span>
    )}
  >
    {children}
  </SentryErrorBoundary>
);

const DummyBoundary = ({ children }: { children: React.Node }): React.Node =>
  children;

/* 🔮 It would be cool to have stronger types here, like enforcing that primary
is a valid CSS color string, etc. Don't think it's currently possible though.

See https://drafts.css-houdini.org/css-typed-om/ for more.
*/
export type Styles = $ReadOnly<{|
  brand: string,
  text: string,
  background: string,
  logoWidth: string,
|}>;

const resolvedTheme = memoize(
  (styles: Styles, darknessLine?: number) => ({
    ...theme,
    ...styles,
    ...derivedColors({ brand: styles.brand, darknessLine }),
  }),
  (styles: Styles, darknessLine?: number): string =>
    JSON.stringify({ styles, darknessLine }),
);

const resolvedL10n = memoize(
  (translations: Translations, locale: string) => ({
    translations,
    locale,
  }),
  (translations: Translations, locale: string) =>
    JSON.stringify({ translations, locale }),
);

export type WrapperProps = $ReadOnly<{|
  translations: Translations,
  locale: string,
  styles: Styles,
  darknessLine?: number,
  className?: string,
  ...SettingsParams,
|}>;

export type Props = $ReadOnly<{|
  ...WrapperProps,
  children: React.Node,
|}>;

export type AppContextOptions = $ReadOnly<{|
  translations: Translations,
  locale: string,
  styles: Styles,
  darknessLine?: number,
  settings: SettingsParams,
|}>;

export type AppContextProps = $ReadOnly<{|
  ...AppContextOptions,
  children: React.Node,
|}>;

export const AppContextProvider = ({
  translations,
  locale,
  styles,
  darknessLine,
  settings,
  children,
}: AppContextProps): React.Node => (
  <L10nProvider value={resolvedL10n(translations, locale)}>
    <SettingsProvider value={settings}>
      <ThemeProvider theme={resolvedTheme(styles, darknessLine)}>
        {children}
      </ThemeProvider>
    </SettingsProvider>
  </L10nProvider>
);

export const ScrambleComWrapper = ({
  translations,
  locale,
  styles,
  darknessLine,
  className,
  children,
  ...rest
}: Props): React.Node => {
  const ErrorBoundary =
    process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test'
      ? // in development, throw errors so they can be picked up by CI
        DummyBoundary
      : // in production, try to handle errors more gracefully
        ScrambleComErrorBoundary;

  return (
    <ErrorBoundary>
      <QueryClientProvider>
        <AppContextProvider
          translations={translations}
          locale={locale}
          darknessLine={darknessLine}
          styles={styles}
          settings={rest}
        >
          <DndProvider backend={HTML5Backend}>
            <BaseCssProvider className={className}>{children}</BaseCssProvider>
          </DndProvider>
        </AppContextProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

const scramble4Connect =
  (
    outerProps: WrapperProps,
  ): (<InputProps: { ... }>(
    WrappedComponent: React.AbstractComponent<InputProps>,
  ) => React$AbstractComponent<any, mixed>) =>
  <InputProps: {}>(WrappedComponent: React.AbstractComponent<InputProps>) => {
    const Scramble4Connect = (props: {
      ...InputProps,
      forwardedRef: React.Ref<any>,
    }) => {
      const { forwardedRef, ...rest } = props;

      return (
        <ScrambleComWrapper {...outerProps}>
          <WrappedComponent {...rest} ref={forwardedRef} />
        </ScrambleComWrapper>
      );
    };

    // eslint-disable-next-line fp/no-mutation
    Scramble4Connect.displayName = `Scramble4Connect(${getDisplayName(
      WrappedComponent,
    )})`;

    // we need to forward the ref to the inner component
    // https://reactjs.org/docs/forwarding-refs.html#forwarding-refs-to-dom-components
    return React.forwardRef((props, ref) => (
      <Scramble4Connect {...props} forwardedRef={ref} />
    ));
  };

export default scramble4Connect;
